/**
 * Canonical lifecycle-management status/strategy values, display labels, ordinals,
 * and normalizers shared by Customer Experience and Customer Success.
 *
 * Values mirror the backend enums in Assets.Shared
 * (LifecycleManagementEventStatusType / LifecycleManagementEventStrategyType) and the
 * generated ShiOneClient enums CX consumes. This module is the single source of truth
 * for the FE display of these two properties so the two apps cannot drift.
 */

export const LIFECYCLE_STATUS_VALUES = [
  "NotActioned",
  "Requested",
  "Completed",
  "PastDue",
  "Canceled",
  "Ignore",
  "Quoted",
] as const;
export type LifecycleStatusValue = (typeof LIFECYCLE_STATUS_VALUES)[number];

/**
 * Business-preferred order for presenting statuses in dropdowns (Customer Experience and
 * Customer Success). Kept separate from LIFECYCLE_STATUS_VALUES / lifecycleStatusOrdinal so
 * the UI ordering can change without affecting the numeric payload contract with the backend.
 */
export const LIFECYCLE_STATUS_DISPLAY_ORDER = [
  "NotActioned",
  "Requested",
  "Quoted",
  "Completed",
  "Canceled",
  "PastDue",
  "Ignore",
] as const satisfies readonly LifecycleStatusValue[];

/** Comparator that orders status values by LIFECYCLE_STATUS_DISPLAY_ORDER; unknowns sort last. */
export function compareLifecycleStatusDisplay(a: string, b: string): number {
  const order = LIFECYCLE_STATUS_DISPLAY_ORDER as readonly string[];
  const aIndex = order.indexOf(a);
  const bIndex = order.indexOf(b);
  return (
    (aIndex === -1 ? order.length : aIndex) -
    (bIndex === -1 ? order.length : bIndex)
  );
}

export const LIFECYCLE_STRATEGY_VALUES = [
  "NotSelected",
  "RenewAsIs",
  "RenewWithAdjustments",
  "NotRenewing",
  "EvaluateAlternative",
] as const;
export type LifecycleStrategyValue = (typeof LIFECYCLE_STRATEGY_VALUES)[number];

/**
 * Customer-facing status labels: the canonical vocabulary the backend now persists and
 * returns, shown directly to customers in Customer Experience.
 */
export const lifecycleStatusLabels: Record<LifecycleStatusValue, string> = {
  NotActioned: "No Action Required",
  Requested: "In Progress",
  Completed: "Completed",
  PastDue: "Past Due",
  Canceled: "Canceled",
  Ignore: "Ignore",
  Quoted: "Quoted",
};

/**
 * Customer Success internal status labels, derived from the same stable keys for the
 * internal-facing drawer control. Display only -- never persisted; the customer-facing
 * value is what the backend stores.
 */
export const lifecycleInternalStatusLabels: Record<LifecycleStatusValue, string> = {
  NotActioned: "No Action Required",
  Requested: "Quote Needed",
  Completed: "Quote Won",
  PastDue: "Past Due",
  Canceled: "Quote Lost",
  Ignore: "Ignore",
  Quoted: "Quoted",
};

export const lifecycleStrategyLabels: Record<LifecycleStrategyValue, string> = {
  NotSelected: "Not Selected",
  RenewAsIs: "Renew / Refresh As Is",
  RenewWithAdjustments: "Renew / Refresh With Adjustments",
  NotRenewing: "Not Renewing / Refreshing",
  EvaluateAlternative: "Evaluate Alternative",
};

type LifecycleEventTypeValue = "Refresh" | "Renewal" | "Billing";

/** Ordinals mirror the backend enum declaration order (numeric payload contract). */
export const lifecycleStatusOrdinal: Record<LifecycleStatusValue, number> = {
  NotActioned: 0,
  Requested: 1,
  Completed: 2,
  PastDue: 3,
  Canceled: 4,
  Ignore: 5,
  Quoted: 6,
};

export const lifecycleStrategyOrdinal: Record<LifecycleStrategyValue, number> = {
  NotSelected: 0,
  RenewAsIs: 1,
  RenewWithAdjustments: 2,
  NotRenewing: 3,
  EvaluateAlternative: 4,
};

/** Ordinals mirror the backend LifecycleManagementEventType enum (Refresh=0, Renewal=1, Billing=2). */
 export const lifecycleEventTypeOrdinal: Record<LifecycleEventTypeValue, number> = {
  Refresh: 0,
  Renewal: 1,
  Billing: 2,
};

/** Customer-facing label for a status value (stable enum key). Falls back to the raw value. */
export const getLifecycleStatusLabel = (value: string): string =>
  lifecycleStatusLabels[value as LifecycleStatusValue] ?? value;

/** Customer Success internal label for a status value (stable enum key). Falls back to the raw value. */
export const getLifecycleInternalStatusLabel = (value: string): string =>
  lifecycleInternalStatusLabels[value as LifecycleStatusValue] ?? value;

/**
 * Customer-facing status label for an event's displayed status. A Strategic Future Renewal
 * event has no customer-facing status, so its label is blank. Use this wherever an event's
 * displayed status is shown to customers; status option lists and filters keep using
 * getLifecycleStatusLabel so every choice stays labelled.
 */
export const getEventCustomerStatusLabel = (
  status: string | null | undefined,
  isStrategicFutureRenewal?: boolean | null,
): string => {
  if (!status || isStrategicFutureRenewal) return "";
  return getLifecycleStatusLabel(status);
};

/**
 * True for an activity-log status change with no previous value: it replaced a status no user stored
 * (the Renewals App status or the default). Reverting it would store a manual status that hides the
 * Renewals App status, and there is no way yet to clear a manual status, so it must not be revertable.
 */
export const isStatusChangeWithoutPreviousValue = (
  key: string | null | undefined,
  oldValue: unknown,
): boolean =>
  key?.trim().toLowerCase() === "status" &&
  (oldValue === null ||
    oldValue === undefined ||
    String(oldValue).trim() === "");

export const getLifecycleStrategyLabel = (value: string): string =>
  lifecycleStrategyLabels[value as LifecycleStrategyValue] ?? value;

const normalizeKey = (input: string): string =>
  input.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Resolves an incoming string (raw enum key OR human-readable label, any spacing/casing)
 * to a canonical value. Returns the fallback for empty or unrecognized input.
 */
function resolveLifecycleValue<T extends string, F>(
  input: string | null | undefined,
  values: readonly string[],
  labels: Record<string, string>,
  fallback: F,
): T | F {
  if (!input?.trim()) return fallback;
  const normalized = normalizeKey(input);
  const byValue = values.find((value) => normalizeKey(value) === normalized);
  if (byValue) return byValue as T;
  const byLabel = values.find(
    (value) => normalizeKey(labels[value]) === normalized,
  );
  return (byLabel as T | undefined) ?? fallback;
}

/**
 * Normalizes a strategy string (label or enum key) to a canonical value. Generic so callers
 * can constrain the result to their own enum type (e.g. the CX generated enum) via the
 * fallback argument. Unrecognized/empty input resolves to NotSelected.
 */
export function normalizeLifecycleStrategy<T extends string = LifecycleStrategyValue>(
  input?: string | null,
  fallback: T = "NotSelected" as T,
): T {
  return resolveLifecycleValue<T, T>(
    input,
    LIFECYCLE_STRATEGY_VALUES,
    lifecycleStrategyLabels,
    fallback,
  );
}

/**
 * Normalizes a status string (label or enum key) to a canonical value. Generic so callers
 * can constrain the result to their own enum type via the fallback argument.
 */
export function normalizeLifecycleStatus<T extends string = LifecycleStatusValue>(
  input?: string | null,
  fallback?: T,
): T | undefined {
  return resolveLifecycleValue<T, T | undefined>(
    input,
    LIFECYCLE_STATUS_VALUES,
    lifecycleStatusLabels,
    fallback,
  );
}
